<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { historyItems as items } from '@/data/historyItems';
import { plainText } from '@/utils/text';
import { useWindowSize } from '@vueuse/core';
const { width } = useWindowSize();
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
const modules = [Navigation];
const swiperOptions = computed(() => {
  return {
    modules: modules,
    slidesPerView: 1.2,
    spaceBetween: 9,
    navigation: {
      nextEl: '.history__slider-next',
      prevEl: '.history__slider-prev',
      disabledClass: '_disabled'
    },
    pagination: {
      clickable: true,
    },
    breakpoints: {
      '768': {
        spaceBetween: 16,
        slidesPerView: 2,
      },
      '1024': {
        spaceBetween: 16,
        slidesPerView: 3,
      },
    },
  }
})
const currentPlace = ref('samara');
const swiperInstance = ref(null);
const pastUntilIndex = ref(0);

const updatePastIndex = (swiper) => {
  const slidesPerView = typeof swiper.params.slidesPerView === 'number'
    ? swiper.params.slidesPerView
    : 1;
  pastUntilIndex.value = swiper.activeIndex + Math.floor(slidesPerView) - 1;
};

const onSwiperInit = (swiper) => {
  swiperInstance.value = swiper;
  updatePastIndex(swiper);
};

const onSlideChange = (swiper) => {
  updatePastIndex(swiper);
};

const goToYear = (index) => {
  if (!swiperInstance.value) return;
  const slidesPerView = typeof swiperInstance.value.params.slidesPerView === 'number'
    ? swiperInstance.value.params.slidesPerView
    : 1;
  const targetIndex = Math.max(0, index - (Math.floor(slidesPerView) - 1));
  swiperInstance.value.slideTo(targetIndex);
};

const placeNames = {
  samara: 'Самара',
  perm: 'Пермь',
};

// alt для фотографии слайда: город, год и заголовок события.
const slideAlt = (slide) =>
  `${placeNames[currentPlace.value]}, ${slide.year} — ${plainText(slide.title)}`;

const getImage = (place, filename) => {
  try {
    return require(`@/assets/img/history/${place}/${filename}`);
  } catch {
    return null;
  }
};

watch(currentPlace, async () => {
  await nextTick();
  if (swiperInstance.value) {
    swiperInstance.value.slideTo(0, 0);
    updatePastIndex(swiperInstance.value);
  }
});

watch(width, async () => {
  await nextTick();
  if (swiperInstance.value) {
    updatePastIndex(swiperInstance.value);
  }
});
</script>
<template>
  <div class="history">
    <img src="@/assets/img/decor1.png" alt="" class="history__decor" loading="lazy">
    <div class="history__inner">
      <h2 class="history__title">
        <img src="@/assets/img/history-title.png" alt="История фабрик в России">
      </h2>
      <div class="history__switch">
        <div class="history__switch-inner">
          <span :class="{'_active': currentPlace === 'samara' }" @click="currentPlace = 'samara'">Самара</span>
          <span :class="{'_active': currentPlace === 'perm' }" @click="currentPlace = 'perm'">Пермь</span>
        </div>
      </div>
      <div :class="['history__timeline', `_${currentPlace}`]">
        <svg xmlns="http://www.w3.org/2000/svg" width="1359" height="108" viewBox="0 0 1359 108" fill="none" preserveAspectRatio="none" v-if="width > 767">
          <path d="M1358 21.4255C1280.42 4.38178 1200.39 0.717884 1170.07 1.0164C901.029 3.46581 644.29 107.408 455.774 106.999C313.299 106.69 284.499 53.235 136.641 39.482C98.4941 35.7386 29.6524 34.8028 0 34.8028" stroke="#642000" stroke-width="2"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="344" height="68" viewBox="0 0 344 68" fill="none" preserveAspectRatio="none" v-else>
          <path d="M343 13.6969C323.406 3.10214 303.191 0.82458 295.533 1.01014C227.579 2.53275 162.733 67.1454 115.118 66.8911C79.1322 66.6989 71.8579 33.4704 34.5126 24.9212C24.8774 22.5942 7.48954 22.0125 2.00272e-05 22.0125" stroke="#642000" stroke-width="2"/>
        </svg>
        <div class="history__timeline-years">
          <div
              v-for="(item, index) in items[currentPlace]"
              :key="index"
              :class="['history__timeline-year', `_${index + 1}`, { _past: index <= pastUntilIndex }]"
              @click="goToYear(index)"
          >
            <span></span>
            {{ item.year }}
          </div>
        </div>
      </div>
      <div class="history__slider-wrap">
        <div class="history__slider-nav">
          <span class="history__slider-prev">
            <svg width="11" height="18" viewBox="0 0 11 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.41406 1L1.41406 9L9.41406 17" stroke="#642000" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          <span class="history__slider-next">
            <svg width="11" height="18" viewBox="0 0 11 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L9 9L1 17" stroke="#642000" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
        </div>
        <Swiper
            v-bind="swiperOptions"
            class="history__slider"
            @swiper="onSwiperInit"
            @slideChange="onSlideChange"
        >
          <SwiperSlide v-for="(slide, slideIndex) in items[currentPlace]" :key="slideIndex" class="history__card">
            <img
                v-if="getImage(currentPlace, slide.image)"
                :src="getImage(currentPlace, slide.image)"
                :alt="slideAlt(slide)"
                loading="lazy"
                :class="['history__card-image', {'_alt': slide?.imageAlt}]"
                :key="currentPlace + slideIndex"
            />
            <div class="history__card-text">
              <h3>{{ slide.year }}</h3>
              <h2 v-html="slide.title"></h2>
              <p v-html="slide.text"></p>
              <p v-if="slide?.note" v-html="slide.note" class="_note"></p>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>
</template>